import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { Button, CheckBox, ControlledTooltips, Link, Modal, NoContent, ReactTable } from '../../../components/shared';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { COLUMNS } from '../../../constants/tableColumns';
import { routeConstants } from '../../../constants/routeConstants';
import {
  useBulkUpdateDateEnteredMutation,
  useGetAllCollectionsQuery,
} from '../../../services/CollectionService/CollectionService';
import { COLLECTION_RECONCILIATION_SUBMITTED } from '../../../constants/CommonConstants';
import {
  checkObjectNotEmpty,
  dateFormatForApi,
  getControlledTooltipValueAndCellValue,
  roundOffValue,
} from '../../../utils/common-methods';
import useStyles from '../style';
import { setReconciliationFilters } from '../../../redux-slice/collectionFilters';
import { useDispatch } from 'react-redux';
import BulkUpdateDateEnteredModal from './BulkUpdateDateEnteredModal';
import { TableSortLabel } from '@mui/material';
import { useSelector } from 'react-redux';

export default function SubmittedCollectionsTable({
  updateDateEnteredSelected = false,
  searchParams,
  reconciliation,
  setTotalData = () => {},
  onCancelPress = () => {},
  onApprovePress = () => {},
}) {
  const hideCheckbox = !updateDateEnteredSelected;
  const classes = useStyles();
  const [selectedCollection, setSelectedCollection] = useState({});
  const [isCheckedAll, setIsCheckedAll] = useState(false);
  const [isIndeterminate, setIsIndeterminate] = useState(false);
  const { sort } = useSelector((state) => state.collectionFilter.reconciliation);

  const viewProductTypes = useCallback((productTypeNames) => {
    const { tooltipValue, cellValue } = getControlledTooltipValueAndCellValue(productTypeNames);
    return <ControlledTooltips tooltipValue={tooltipValue}>{cellValue}</ControlledTooltips>;
  }, []);

  const handleSortChange = (column) => {
    const columnConfig = COLUMNS.SUBMITTED_COLLECTIONS_LOCATIONS_TABLE.find((col) => col.accessor === column);
    if (!columnConfig?.sortKey) return;

    const isDesc = sort?.startsWith(columnConfig?.sortKey) && sort?.endsWith('DESC');
    const newSort = `${columnConfig?.sortKey}:${isDesc ? 'ASC' : 'DESC'}`;

    dispatch(setReconciliationFilters({ ...reconciliation, sort: newSort, pageNumber: 1 }));
  };

  const COLLECTION_LOCATIONS_COLUMNS = () => {
    const checkboxColumn = {
      Header: () => (
        <CheckBox
          onChange={(e) => onCheckAllSelect(e.target.checked)}
          checked={isCheckedAll}
          indeterminate={isIndeterminate}
        />
      ),
      id: 'selection',
      Cell: ({ row }) => (
        <CheckBox
          onChange={(e) => onRowSelect(row.original, e.target.checked)}
          checked={selectedCollection[row.original.transactionId]?.selected}
        />
      ),
      width: 50,
    };

    const COLLECTION_COLUMNS = [
      ...COLUMNS.SUBMITTED_COLLECTIONS_LOCATIONS_TABLE.map((col) => ({
        ...col,
        Header: col.sortKey
          ? () => (
              <Box>
                <TableSortLabel
                  sx={{
                    '& .MuiTableSortLabel-icon': {
                      fontSize: '15px',
                      stroke: '#3c3c3c',
                      fill: '#3c3c3c',
                      marginLeft: '10px',
                      marginBottom: '1px',
                    },
                  }}
                  active={sort?.startsWith(col.sortKey)}
                  direction={sort?.endsWith('DESC') ? 'desc' : 'asc'}
                  onClick={() => handleSortChange(col.accessor)}
                >
                  {col.Header}
                </TableSortLabel>
              </Box>
            )
          : col.Header,
      })),
    ];

    return hideCheckbox ? COLLECTION_COLUMNS : [checkboxColumn, ...COLLECTION_COLUMNS];
  };

  const reconciliationLink = useCallback(
    (id, name) => (
      <Link underline="none" to={`${routeConstants.RECONCILIATION_COLLECTION_ROUTE}/${id}`}>
        {name}
      </Link>
    ),
    [],
  );
  const [currentPage, setCurrentPage] = useState(reconciliation?.pageNumber);
  const [perPageNumber, setPerPageNumber] = useState(reconciliation?.rowsPerPage);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();

  const [bulkUpdateDateEnteredMutation] = useBulkUpdateDateEnteredMutation();
  const {
    currentData: latestData,
    isError,
    isSuccess,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetAllCollectionsQuery({
    searchParams,
    page: currentPage,
    perPage: perPageNumber,
    status: COLLECTION_RECONCILIATION_SUBMITTED,
    sort,
  });

  useEffect(() => {
    refetch();
  }, []);

  const tableData = useMemo(() => {
    if (isSuccess && latestData) {
      const rawData = latestData?.collections;
      return rawData.map((data) => {
        const transformedData = { ...data };
        transformedData.locationName = reconciliationLink(
          data.transactionId,
          data.location?.locationName ?? data.locationName ?? 'N/A',
        );
        transformedData.locationType = data.location?.locationType?.name ?? data.locationType ?? 'N/A';
        transformedData.locationRevenue = roundOffValue(data.locationRevenue ?? data.totalRevenue);
        transformedData.serviceRep =
          data.serviceRep ?? (data.user ? `${data.user.firstName} ${data.user.lastName}` : 'N/A');

        // Map IndependentRepUsers array to independentRep names
        if (
          data.IndependentRepUsers &&
          Array.isArray(data.IndependentRepUsers) &&
          data.IndependentRepUsers.length > 0
        ) {
          const repNames = data.IndependentRepUsers.map((rep) =>
            `${rep.userDetails?.firstName || ''} ${rep.userDetails?.lastName || ''}`.trim(),
          ).filter((name) => name);
          if (repNames.length > 0) {
            const { tooltipValue, cellValue } = getControlledTooltipValueAndCellValue(repNames);
            transformedData.independentRep = (
              <ControlledTooltips tooltipValue={tooltipValue}>{cellValue}</ControlledTooltips>
            );
          } else {
            transformedData.independentRep = 'N/A';
          }
        } else if (Array.isArray(data.independentRep) && data.independentRep[0] !== 'N/A') {
          const { tooltipValue, cellValue } = getControlledTooltipValueAndCellValue(data.independentRep);
          transformedData.independentRep = (
            <ControlledTooltips tooltipValue={tooltipValue}>{cellValue}</ControlledTooltips>
          );
        } else {
          transformedData.independentRep = 'N/A';
        }

        // Map productCategories array to productTypes
        if (data.productCategories && Array.isArray(data.productCategories) && data.productCategories.length > 0) {
          const productTypeNames = data.productCategories.map((pc) => pc.name);
          transformedData.productTypes = viewProductTypes(productTypeNames);
        } else {
          transformedData.productTypes = viewProductTypes(
            data.productTypes ?? data.productType?.map((pt) => pt?.name) ?? [],
          );
        }

        transformedData.submissionDate = data.submissionDate ?? data.submittedDate ?? 'N/A';

        return transformedData;
      });
    }
  }, [isSuccess, latestData, reconciliationLink, viewProductTypes]);

  useEffect(() => {
    setTotalData(tableData?.length || 0);
  }, [tableData]);

  const totalCount = useMemo(() => {
    if (isSuccess && latestData) {
      return latestData?.pagination?.total ?? 0;
    }
    return 0;
  }, [isSuccess, latestData]);

  useEffect(() => {
    const initialSelectedCollection = {};
    tableData?.map((item) => {
      if (item.hasError) return;
      initialSelectedCollection[item.transactionId] = {
        ...item,
        selected: false,
      };
    });
    const isIndeterminate = checkIndeterminate(initialSelectedCollection);
    setIsIndeterminate(isIndeterminate);
    const isAllTrue = checkObjectNotEmpty(initialSelectedCollection)
      ? checkIsAllTrue(initialSelectedCollection)
      : false;
    setSelectedCollection(initialSelectedCollection);
    setIsCheckedAll(isAllTrue ? true : false);
  }, [tableData]);

  useEffect(() => {
    setCurrentPage(reconciliation?.pageNumber);
    setPerPageNumber(reconciliation?.rowsPerPage);
  }, [searchParams, sort]);

  const checkIsAllTrue = (selectedCollection) =>
    Object.values(selectedCollection)?.every((item) => item.selected === true);
  const checkIsAllFalse = (selectedCollection) =>
    Object.values(selectedCollection)?.every((item) => item.selected === false);

  const checkIndeterminate = (selectedCollection) => {
    const isAllTrue = checkIsAllTrue(selectedCollection);
    const isAllFalse = checkIsAllFalse(selectedCollection);
    return isAllFalse || isAllTrue ? false : true;
  };

  const onCheckAllSelect = (checked) => {
    const updatedSelectedCollection = { ...selectedCollection };
    Object.keys(updatedSelectedCollection).map((item) => {
      updatedSelectedCollection[item] = { ...updatedSelectedCollection[item], selected: checked };
    });
    setIsIndeterminate(false);
    setSelectedCollection(updatedSelectedCollection);
    setIsCheckedAll(checked);
  };

  const onRowSelect = (row, checked) => {
    const updatedSelectedCollection = { ...selectedCollection, [row.transactionId]: { ...row, selected: checked } };
    const isAllTrue = checkIsAllTrue(updatedSelectedCollection);
    const isAllFalse = checkIsAllFalse(updatedSelectedCollection);
    setSelectedCollection(updatedSelectedCollection);
    setIsCheckedAll(isAllTrue ? true : false);
    setIsIndeterminate(isAllFalse || isAllTrue ? false : true);
  };

  const pageChangeHandler = (currentPage) => {
    dispatch(setReconciliationFilters({ ...reconciliation, pageNumber: currentPage, rowsPerPage: perPageNumber }));
    setCurrentPage(currentPage);
  };

  const perPageChangeHandler = (newPerPage) => {
    dispatch(setReconciliationFilters({ ...reconciliation, rowsPerPage: newPerPage, pageNumber: currentPage }));
    setPerPageNumber(newPerPage);
  };

  const approveDisable = useMemo(
    () => Object.values(selectedCollection).some((item) => item?.selected === true),
    [selectedCollection],
  );

  const resetSelections = () => {
    setSelectedCollection({});
    setIsCheckedAll(false);
    setIsIndeterminate(false);
    setShowApproveModal(false);
    setIsSubmitting(false);
  };

  const onCancelButtonPress = () => {
    resetSelections();
    onCancelPress();
  };

  const onApproveButtonPress = (updatedDateEntered) => {
    const transactionIds = [];
    Object.values(selectedCollection).map((collection) => {
      if (collection.selected) {
        transactionIds.push(collection.transactionId);
      }
    });

    const data = { transactionIds: transactionIds, dateEntered: dateFormatForApi(new Date(updatedDateEntered)) };

    bulkUpdateDateEnteredMutation(data)
      .then(() => {
        resetSelections();
        onApprovePress();
      })
      .catch(() => {
        setIsSubmitting(false);
        resetSelections();
      });
  };

  const BottomExtraBtn = () => (
    <Box sx={{ marginLeft: 3 }}>
      <Button onClick={() => setShowApproveModal(true)} variant="contained" disabled={!approveDisable}>
        UPDATE
      </Button>
      <Button onClick={onCancelButtonPress}>CANCEL</Button>
    </Box>
  );

  return (
    <Box className={classes.initialTableCheckbox}>
      {(isLoading || isFetching) && <LoadingSpinner containerHeight={300} />}
      {isError && (
        <NoContent
          title="No Collection Found"
          desc={(isError && error?.data?.message) || <>There are no collection present with this filter.</>}
        />
      )}
      {!isLoading && !isError && !isFetching && tableData && (
        <ReactTable
          data={tableData}
          columns={COLLECTION_LOCATIONS_COLUMNS()}
          pagination
          manualPagination
          totalCount={totalCount}
          pageNumberForTablePagination={currentPage - 1}
          onPageChange={pageChangeHandler}
          onPerPageChange={perPageChangeHandler}
          perPage={perPageNumber}
          bottomExtraBtn={hideCheckbox ? null : <BottomExtraBtn />}
          disablePagination={updateDateEnteredSelected}
        />
      )}
      <BulkUpdateDateEnteredModal
        showApproveModal={showApproveModal}
        onApproveButtonPress={onApproveButtonPress}
        setShowApproveModal={setShowApproveModal}
        selectedCollection={selectedCollection}
      />
      {isSubmitting && (
        <Modal open={isSubmitting} customWidth="395px">
          <LoadingSpinner containerHeight={300} />
        </Modal>
      )}
    </Box>
  );
}
